import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Calendar as CalendarIcon, 
  ArrowLeft, 
  RotateCcw, 
  Printer, 
  Plus, 
  Trash2,
  CheckCircle2, 
  Sparkles 
} from 'lucide-react';

export const CalendarGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth()); // 0-11
  const [startDay, setStartDay] = useState(0); // 0: Sunday, 1: Monday

  const [notes, setNotes] = useState({
    '2026-05-14': 'Quarterly Strategy Review',
    '2026-05-25': 'Memorial Day Holiday',
    '2026-06-01': 'Team Offsite Event'
  });

  const [activeNoteDate, setActiveNoteDate] = useState('');
  const [noteInput, setNoteInput] = useState('');

  const monthsList = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysHeader = startDay === 0 
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Calendar Math
  const getCalendarDays = () => {
    let firstDayIndex = new Date(year, month, 1).getDay(); // 0-6
    if (startDay === 1) {
      firstDayIndex = (firstDayIndex + 6) % 7;
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    let grid = [];

    // Previous month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      let d = daysInPrevMonth - i;
      let m = month === 0 ? 11 : month - 1;
      let y = month === 0 ? year - 1 : year;
      let dateStr = `${y}-${(m+1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
      grid.push({ day: d, isCurrentMonth: false, dateStr });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      let dateStr = `${year}-${(month+1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      grid.push({ day: i, isCurrentMonth: true, dateStr });
    }

    // Next month padding to fill grid row
    let remainingCells = (7 - (grid.length % 7)) % 7;
    for (let i = 1; i <= remainingCells; i++) {
      let m = month === 11 ? 0 : month + 1;
      let y = month === 11 ? year + 1 : year;
      let dateStr = `${y}-${(m+1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      grid.push({ day: i, isCurrentMonth: false, dateStr });
    }

    return grid;
  };

  const calendarGrid = getCalendarDays();

  const handleDayClick = (dateStr) => {
    setActiveNoteDate(dateStr);
    setNoteInput(notes[dateStr] || '');
  };

  const saveNote = (e) => {
    if (e) e.preventDefault();
    if (!activeNoteDate) return;
    if (!noteInput.trim()) {
      const copy = { ...notes };
      delete copy[activeNoteDate];
      setNotes(copy);
    } else {
      setNotes({ ...notes, [activeNoteDate]: noteInput.trim() });
    }
    setActiveNoteDate('');
    setNoteInput('');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    setYear(new Date().getFullYear());
    setMonth(new Date().getMonth());
    setNotes({});
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Printable Calendar Generator Online" 
        description="Generate customized monthly and yearly calendars. Highlight notes, track holidays, and export clean printable grid layouts instantly."
        keywords="calendar generator, printable calendar online, monthly grid maker, custom holiday calendar, event calendar builder"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }} className="no-print">
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Calendar Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }} className="no-print">
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Customizable Monthly Calendar Generator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Create clean, high-resolution printable calendar grid views with custom attached event highlights.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Setup Controls */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }} className="no-print">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CalendarIcon size={18} style={{ color: 'var(--primary)' }} /> Setup Parameters
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Year</label>
                <input 
                  type="number" 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value) || 2026)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', fontWeight: 700, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Month</label>
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  {monthsList.map((m, idx) => (
                    <option key={m} value={idx}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Starting Weekday</label>
              <select 
                value={startDay} 
                onChange={(e) => setStartDay(parseInt(e.target.value))}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
              </select>
            </div>

            {/* Note Editor Modal Box */}
            {activeNoteDate && (
              <div style={{ background: 'rgba(5, 117, 251, 0.08)', padding: '16px', borderRadius: '14px', border: '1px solid var(--primary)' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)', marginBottom: '8px' }}>Attach Note: {activeNoteDate}</div>
                <form onSubmit={saveNote} style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    value={noteInput} 
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="e.g. Doctor appointment, Holiday"
                    style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '0.85rem', outline: 'none', background: '#ffffff' }}
                  />
                  <button type="submit" style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '0 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                    Save
                  </button>
                </form>
              </div>
            )}

            <button
              onClick={handlePrint}
              style={{ width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: '#10b981', color: '#ffffff', fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              <Printer size={20} /> Print Grid View
            </button>
          </div>
        </div>

        {/* Right Side: Grid Display */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '32px', boxShadow: 'var(--shadow-sm)' }} className="print-container">
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '2px solid var(--border-color)', paddingBottom: '16px' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0 }}>
                {monthsList[month]} {year}
              </h2>
              <div style={{ display: 'flex', gap: '8px' }} className="no-print">
                <button 
                  onClick={() => { let m = month === 0 ? 11 : month - 1; let y = month === 0 ? year - 1 : year; setMonth(m); setYear(y); }}
                  style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '8px 16px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Prev
                </button>
                <button 
                  onClick={() => { let m = month === 11 ? 0 : month + 1; let y = month === 11 ? year + 1 : year; setMonth(m); setYear(y); }}
                  style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '8px 16px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Next
                </button>
              </div>
            </div>

            {/* Calendar Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginBottom: '8px', textAlign: 'center' }}>
              {daysHeader.map(h => (
                <div key={h} style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-secondary)', padding: '8px 0', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Calendar Body Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
              {calendarGrid.map((c, idx) => {
                const note = notes[c.dateStr];
                const isToday = c.dateStr === new Date().toISOString().split('T')[0];

                return (
                  <div
                    key={idx}
                    onClick={() => handleDayClick(c.dateStr)}
                    style={{
                      minHeight: '100px',
                      background: c.isCurrentMonth ? '#ffffff' : '#f8fafc',
                      border: `1px solid ${isToday ? 'var(--primary)' : 'var(--border-color)'}`,
                      borderRadius: '12px',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      opacity: c.isCurrentMonth ? 1 : 0.5,
                      boxShadow: isToday ? '0 0 0 2px var(--primary)' : 'none',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="calendar-cell"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 800, color: isToday ? 'var(--primary)' : 'var(--text-primary)' }}>
                        {c.day}
                      </span>
                    </div>

                    {note && (
                      <div style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', padding: '4px 6px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {note}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: '28px', textAlign: 'center' }} className="no-print">
              <AdToast message={toast} />
              <button 
                onClick={withAd('cal-export-btn', () => alert("Calendar schedule exported."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Printable Layout
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
        @media print {
          .no-print { display: none !important; }
          .print-container { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .calendar-cell { border: 1px solid #000 !important; min-height: 120px !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="calendar-generator" />
    </div>
  );
};

export default CalendarGenerator;
